import React, { useState, useEffect } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
         ResponsiveContainer, Legend, FunnelChart, Funnel, LabelList } from "recharts";
import { TrendingUp, Users2, Award, Target, Clock } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../../../../../services/config';
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

const CustomTooltip = ({ active, payload, label }) => {
  const { T } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.card, border:`1px solid ${T.cyan}26`, borderRadius:8,
      padding:"10px 14px", fontFamily:"Outfit,sans-serif", fontSize:12 }}>
      {label && <div style={{ color:T.muted, marginBottom:4 }}>{label}</div>}
      {payload.map((p,i) => (
        <div key={i} style={{ color:p.color||T.text }}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
};

export default function PerformanceDashboard() {
  const { T } = useTheme();
  const RATING_COLORS = [T.green, T.cyan, T.amber, T.red];
  const STATUS_COLORS = [T.muted, T.amber, T.cyan, T.green, T.purple];

  const [data,    setData]    = useState(null);
  const [cycles,  setCycles]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, cRes] = await Promise.all([
          axios.get(`${API}/performance/dashboard`, ax()),
          axios.get(`${API}/performance/cycles`,    ax()),
        ]);
        setData(dRes.data   || {});
        setCycles(cRes.data || []);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ padding:40, textAlign:"center", color:T.muted, fontFamily:"Outfit,sans-serif" }}>Loading…</div>;
  if (!data) return null;

  const s = data.status_breakdown || {};
  const rd = data.rating_distribution || {};

  // Chart data
  const statusData = [
    { name:"Pending",          value:s.pending||0,          color:STATUS_COLORS[0] },
    { name:"Self Submitted",   value:s.self_submitted||0,   color:STATUS_COLORS[1] },
    { name:"Mgr Reviewed",     value:s.manager_reviewed||0, color:STATUS_COLORS[2] },
    { name:"HR Finalized",     value:s.hr_finalized||0,     color:STATUS_COLORS[3] },
  ].filter(d => d.value > 0);

  const ratingData = [
    { name:"Exceeds",     value:rd["Exceeds Expectations"]||0,  color:RATING_COLORS[0] },
    { name:"Meets",       value:rd["Meets Expectations"]||0,    color:RATING_COLORS[1] },
    { name:"Needs Impr.", value:rd["Needs Improvement"]||0,     color:RATING_COLORS[2] },
    { name:"Unacceptable",value:rd["Unacceptable"]||0,          color:RATING_COLORS[3] },
  ].filter(d => d.value > 0);

  const cycleBarData = cycles.map(c => ({
    name:      c.name.length > 18 ? c.name.slice(0,16)+"…" : c.name,
    Pending:   c.stats?.pending          || 0,
    Submitted: c.stats?.submitted        || 0,
    Reviewed:  c.stats?.manager_reviewed || 0,
    Finalized: c.stats?.finalized        || 0,
  }));

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:20 };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:11, color:T.amber, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>PERFORMANCE</div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Performance Overview</h2>
        <p style={{ fontSize:13, color:T.muted, margin:0 }}>Appraisal analytics and team performance at a glance</p>
      </div>

      {/* KPI cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:24 }}>
        {[
          { label:"Total Cycles",    val:data.cycles||0,                icon:Target,   color:T.cyan },
          { label:"Active Cycles",   val:data.active_cycles||0,         icon:TrendingUp,color:T.green},
          { label:"Total Appraisals",val:data.total_appraisals||0,      icon:Users2,   color:T.purple },
          { label:"HR Finalized",    val:s.hr_finalized||0,             icon:Award,    color:T.amber },
        ].map(({ label, val, icon: Icon, color }) => (
          <div key={label} style={{ ...card, borderTop:`3px solid ${color}`, display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:42, height:42, borderRadius:10, background:`${color}15`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Icon size={20} style={{ color }}/>
            </div>
            <div>
              <div style={{ fontSize:24, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
              <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:2 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row 1 */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Appraisal Status Donut */}
        <div style={card}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:16 }}>Appraisal Status Breakdown</div>
          {statusData.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:T.dim }}>No appraisals yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                  dataKey="value" paddingAngle={3}>
                  {statusData.map((d,i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Tooltip content={<CustomTooltip/>}/>
                <Legend formatter={(v) => <span style={{ color:T.muted, fontSize:12 }}>{v}</span>}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Rating Distribution */}
        <div style={card}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:16 }}>Rating Distribution</div>
          {ratingData.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:T.dim }}>No finalized appraisals yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ratingData} barSize={36}>
                <XAxis dataKey="name" tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Bar dataKey="value" radius={[6,6,0,0]}>
                  {ratingData.map((d,i) => <Cell key={i} fill={d.color}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Cycle Progress Chart */}
      {cycleBarData.length > 0 && (
        <div style={card}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:16 }}>Progress by Cycle</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={cycleBarData} barSize={18} barGap={4}>
              <XAxis dataKey="name" tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false}/>
              <Tooltip content={<CustomTooltip/>} cursor={{ fill:T.surface_faint }}/>
              <Legend formatter={(v) => <span style={{ color:T.muted, fontSize:11 }}>{v}</span>}/>
              <Bar dataKey="Pending"   fill={STATUS_COLORS[0]} radius={[4,4,0,0]}/>
              <Bar dataKey="Submitted" fill={STATUS_COLORS[1]} radius={[4,4,0,0]}/>
              <Bar dataKey="Reviewed"  fill={STATUS_COLORS[2]} radius={[4,4,0,0]}/>
              <Bar dataKey="Finalized" fill={STATUS_COLORS[3]} radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Active cycle names */}
      {(data.active_cycle_names||[]).length > 0 && (
        <div style={{ ...card, marginTop:16, display:"flex", gap:12, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ fontSize:12, color:T.muted }}>Active cycles:</div>
          {data.active_cycle_names.map(n => (
            <span key={n} style={{ fontSize:12, padding:"3px 10px", borderRadius:20,
              background:`${T.green}1A`, color:T.green, fontWeight:600 }}>{n}</span>
          ))}
        </div>
      )}
    </div>
  );
}
